import { uniqBy } from "es-toolkit";

import { isDefined } from "@/utils/object";

interface CollectionWrapper<T> {
  value(): T[];

  map<U>(fn: (item: T, index: number, array: readonly T[]) => U): CollectionWrapper<U>;
  filter(predicate: (value: T, index: number, array: readonly T[]) => boolean): CollectionWrapper<T>;
  filter<U extends T>(predicate: (value: T, index: number, array: readonly T[]) => value is U): CollectionWrapper<U>;
  /**
   * A map + filter(isDefined) in a single pass
   */
  filterMap<U>(fn: (item: T, index: number, array: readonly T[]) => U | null | undefined): CollectionWrapper<U>;
  flatMap<U>(fn: (item: T, index: number, array: readonly T[]) => U[]): CollectionWrapper<U>;
  groupBy<K>(keyFn: (item: T) => K): MapWrapper<K, T[]>;
  slice(start: number, end?: number): CollectionWrapper<T>;
  /**
   * Sorts the collection in ascending order.
   *
   * Only available when we're dealing with an array of `number` or `string`.
   */
  sort(): T extends number | string ? CollectionWrapper<T> : never;
  sortBy<K>(keyFn: (item: T) => K): CollectionWrapper<T>;
  uniqBy<K>(keyFn: (item: T) => K): CollectionWrapper<T>;
}

interface MapWrapper<K, V> {
  value(): Map<K, V>;
  keys(): CollectionWrapper<K>;
  values(): CollectionWrapper<V>;

  mapValues<U>(fn: (value: V, key: K, map: ReadonlyMap<K, V>) => U): MapWrapper<K, U>;
}

/**
 * A wrapper to enable chaining of common array operations.
 *
 * Do : add additional methods when they are **commonly used** and often chained with other collection methods.
 * Do not : add every array util you can think of : the goal is not to force everyone to use this wrapper.
 */
export function chain<T>(array: T[]): CollectionWrapper<T> {
  return {
    value() {
      return array;
    },
    map(fn) {
      return chain(array.map(fn));
    },
    filter<U extends T>(predicate: (value: T, index: number, array: readonly T[]) => value is U) {
      return chain(array.filter(predicate));
    },
    filterMap<U>(predicate: (item: T, index: number, array: T[]) => U | null | undefined) {
      const result: U[] = [];

      for (const [index, item] of array.entries()) {
        const mapped = predicate(item, index, array);
        if (isDefined(mapped)) {
          result.push(mapped);
        }
      }

      return chain(result);
    },
    flatMap(fn) {
      return chain(array.flatMap(fn));
    },
    groupBy<K>(keyFn: (item: T) => K) {
      const map = new Map<K, T[]>();

      for (const item of array) {
        const key = keyFn(item);
        const group = map.get(key) ?? [];
        group.push(item);
        map.set(key, group);
      }

      return chainMap(map);
    },
    slice(start, end) {
      return chain(array.slice(start, end));
    },
    sort() {
      return chain(
        array.slice().sort((a, b) => {
          if (a < b) return -1;
          if (a > b) return 1;
          return 0;
        }),
      ) as ReturnType<CollectionWrapper<T>["sort"]>;
    },
    sortBy(keyFn) {
      const sorted = array.slice().sort((a, b) => {
        const valueA = keyFn(a);
        const valueB = keyFn(b);
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;

        return 0;
      });

      return chain(sorted);
    },
    uniqBy(keyFn) {
      return chain(uniqBy(array, keyFn));
    },
  };
}

export function chainMap<K, V>(map: Map<K, V>): MapWrapper<K, V> {
  return {
    value() {
      return map;
    },
    keys() {
      return chain(Array.from(map.keys()));
    },
    values() {
      return chain(Array.from(map.values()));
    },

    mapValues<U>(fn: (value: V, key: K, map: ReadonlyMap<K, V>) => U) {
      const result = new Map<K, U>();

      for (const [key, value] of map.entries()) {
        result.set(key, fn(value, key, map));
      }

      return chainMap(result);
    },
  };
}
